/**
 * Live Trivia Events screen.
 *
 * Data sources
 *   - GET /admin/live                 (event list — reused as-is)
 *   - GET /admin/live/{id}/payouts    (per-event payout table + house economics +
 *                                      per-event power-up usage totals)
 *
 * Both tables (event list, per-event payout breakdown) are click-sortable
 * via the shared mountSortableTable helper. A row click on the event list
 * opens the payout drill-down inline; deep-link support is ctx.liveId
 * (from /admin/v2/live/:id).
 */

import { api, ApiError } from '../../api.js';
import { toast } from '../../components/toast.js';
import {
  errorState,
  escape,
  fmtBps,
  fmtInt,
  fmtKobo,
  fmtRelTime,
  kpi,
  mountSortableTable,
  skeletonGrid,
} from './common.js';
import { integrityChips, integritySortKey } from './integrity.js';

export async function renderLive(host, ctx) {
  host.innerHTML = skeletonGrid(3);
  try {
    const events = await api('/admin/live');
    if (ctx.liveId) {
      host.innerHTML = await _renderDrilldown(events, ctx);
      _wireDrilldown(host, ctx);
      return;
    }
    host.innerHTML = _renderList(events, ctx);
    _wireList(host, ctx, events);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      ctx.navigate('/login');
      return;
    }
    toast.fromError(err);
    host.innerHTML = errorState(err);
  }
}

// ---------------------------------------------------------------------------
// Event list — click a row to drill down.
// ---------------------------------------------------------------------------

function _renderList(events) {
  if (!events || !events.length) {
    return `<div class="adm2-empty">
      <div class="adm2-empty-icon">📅</div>
      <div class="adm2-empty-title">No live events yet</div>
      <div class="adm2-empty-sub">Create one from the old admin's Live page — this v2 screen is read-only.</div>
    </div>`;
  }
  // Summary strip — totals aggregated across every event on the list.
  // Gross revenue is the sum of the three per-event streams (entry fees
  // net of refunds + rake / house cut + estimated power-up revenue),
  // which the backend already computes per row.
  const totalEvents = events.length;
  const totalParticipants = events.reduce((n, e) => n + Number(e.participants_count || 0), 0);
  const totalPrizePool = events.reduce((n, e) => n + Number(e.prize_kobo || 0), 0);
  const totalEntryRevenue = events.reduce((n, e) => n + Number(e.entry_revenue_kobo || 0), 0);
  const totalHouseCut = events.reduce((n, e) => n + Number(e.house_cut_kobo || 0), 0);
  const totalPowerupEst = events.reduce(
    (n, e) => n + Number(e.powerup_revenue_est_kobo || 0),
    0,
  );
  const totalGross = events.reduce((n, e) => n + Number(e.gross_revenue_kobo || 0), 0);
  return `
    <div class="adm2-section-label">Live trivia summary</div>
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(210px,1fr));">
      ${kpi({ label: 'Events', value: fmtInt(totalEvents), hint: 'all statuses' })}
      ${kpi({ label: 'Participants', value: fmtInt(totalParticipants), hint: 'across events' })}
      ${kpi({ label: 'Prize pool', value: fmtKobo(totalPrizePool), hint: 'gross' })}
      ${kpi({ label: 'Entry revenue', value: fmtKobo(totalEntryRevenue), hint: 'entries − refunds' })}
      ${kpi({ label: 'Rake · house cut', value: fmtKobo(totalHouseCut), hint: '~8% of prize pool' })}
      ${kpi({ label: 'Power-up revenue', value: fmtKobo(totalPowerupEst), hint: 'estimated · in-event units × avg price' })}
      ${kpi({ label: 'Gross revenue', value: fmtKobo(totalGross), hint: 'entry + rake + power-ups', hero: true })}
    </div>

    <div class="adm2-panel" style="padding:0;overflow:hidden;margin-top:18px;">
      <div style="padding:18px 20px;border-bottom:1px solid var(--a-border);">
        <div class="adm2-panel-title">Live events</div>
        <div class="adm2-panel-sub">click a row to see payout breakdown &amp; house economics · click a header to sort</div>
      </div>
      <div id="adm2-live-list" style="padding:8px 4px;"></div>
    </div>
  `;
}

function _wireList(host, ctx, events) {
  const columns = [
    {
      key: 'title',
      label: 'Event',
      text: true,
      get: (e) => (e.title || '').toLowerCase(),
      cell: (e) => `
        <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;">
          <span style="font:600 14px 'IBM Plex Sans',sans-serif;color:var(--a-ink);">${escape(e.title)}</span>
          ${e.pinned ? '<span class="adm2-chip w">PINNED</span>' : ''}
        </div>
        <div style="font:400 12px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-top:4px;">
          ${escape((e.id || '').slice(0, 8))} · scheduled ${escape(fmtRelTime(e.scheduled_at))}
        </div>
      `,
    },
    {
      key: 'participants',
      label: 'Participants',
      right: true,
      get: (e) => Number(e.participants_count || 0),
      cell: (e) => `<span class="mono">${escape(fmtInt(e.participants_count))}</span>`,
      className: 'mono',
    },
    {
      key: 'prize',
      label: 'Prize pool',
      right: true,
      get: (e) => Number(e.prize_kobo || 0),
      cell: (e) => escape(fmtKobo(e.prize_kobo)),
      className: 'mono',
    },
    {
      key: 'entry_fee',
      label: 'Entry fee',
      right: true,
      get: (e) => Number(e.entry_fee_kobo || 0),
      cell: (e) => escape(fmtKobo(e.entry_fee_kobo)),
      className: 'mono',
    },
    {
      key: 'entry_revenue',
      label: 'Entry revenue',
      right: true,
      get: (e) => Number(e.entry_revenue_kobo || 0),
      cell: (e) => `<span style="color:var(--a-ok);">${escape(fmtKobo(e.entry_revenue_kobo))}</span>`,
      className: 'mono',
    },
    {
      key: 'powerup_revenue_est',
      label: 'Power-up rev.',
      right: true,
      get: (e) => Number(e.powerup_revenue_est_kobo || 0),
      cell: (e) => escape(fmtKobo(e.powerup_revenue_est_kobo)),
      className: 'mono',
    },
    {
      key: 'gross_revenue',
      label: 'Gross revenue',
      right: true,
      get: (e) => Number(e.gross_revenue_kobo || 0),
      cell: (e) => `<span style="color:var(--a-warm-ink);font-weight:600;">${escape(fmtKobo(e.gross_revenue_kobo))}</span>`,
      className: 'mono',
    },
    {
      key: 'scheduled_at',
      label: 'Scheduled',
      right: true,
      get: (e) => (e.scheduled_at ? new Date(e.scheduled_at).getTime() : 0),
      cell: (e) => escape(fmtRelTime(e.scheduled_at)),
    },
    {
      key: 'status',
      label: 'Status',
      right: true,
      text: true,
      get: (e) => e.status || '',
      cell: (e) => `<span class="adm2-chip ${_statusTone(e.status)}">${escape(e.status || '—')}</span>`,
    },
  ];
  const listHost = host.querySelector('#adm2-live-list');
  mountSortableTable(listHost, {
    columns,
    rows: events,
    defaultSort: { key: 'scheduled_at', dir: 'desc' },
    minWidth: 1280,
    onRowClick: (row) => ctx.navigate(`/admin/v2/live/${row.id}`),
    pageSizeKey: 'live.events',
  });
}

// ---------------------------------------------------------------------------
// Drill-down — per-event economics + power-ups used + payout table.
// ---------------------------------------------------------------------------

async function _renderDrilldown(events, ctx) {
  const event = events.find((e) => e.id === ctx.liveId);
  if (!event) {
    return `<a href="/admin/v2/live" data-link class="adm2-btn" style="display:inline-block;margin-bottom:12px;">‹ All events</a>
      <div class="adm2-error">Live event not found.</div>`;
  }
  // /stats has every event-level number the drill-down needs
  // (economics, power-ups used, participant + new-user counts).
  // /participants renders the per-user table asynchronously via
  // _wireDrilldown → _mountParticipantsTable — no need to fetch it
  // here.
  let stats = null;
  try {
    stats = await api(`/admin/live/${encodeURIComponent(event.id)}/stats`);
  } catch {
    stats = null;
  }
  const grossKobo = stats?.prize_gross_kobo ?? event.prize_kobo;
  const distributedKobo = stats?.prize_distributed_kobo ?? 0;
  const houseCutKobo = stats?.house_cut_kobo ?? 0;
  const houseCutBps = stats?.house_cut_bps ?? 0;
  const bonusBps = stats?.bonus_pct_bps ?? event.bonus_pct_bps ?? 0;
  const erasersUsed = stats?.erasers_used_in_event ?? 0;
  const fiftyUsed = stats?.fiftyfifty_used_in_event ?? 0;
  const entryRevenueKobo = stats?.entry_revenue_kobo ?? event.entry_revenue_kobo ?? 0;
  const eraserRevEstKobo = stats?.eraser_revenue_est_kobo ?? event.eraser_revenue_est_kobo ?? 0;
  const fiftyRevEstKobo = stats?.fiftyfifty_revenue_est_kobo ?? event.fiftyfifty_revenue_est_kobo ?? 0;
  const powerupRevEstKobo = stats?.powerup_revenue_est_kobo ?? event.powerup_revenue_est_kobo ?? 0;
  const grossRevenueKobo = stats?.gross_revenue_kobo ?? event.gross_revenue_kobo ?? 0;
  const avgEraserPriceKobo = stats?.avg_eraser_price_kobo ?? 0;
  const avgFiftyPriceKobo = stats?.avg_fiftyfifty_price_kobo ?? 0;
  const participantCount = stats?.participant_count ?? event.participants_count ?? 0;
  const newParticipantCount = stats?.new_participant_count ?? 0;

  return `
    <a href="/admin/v2/live" data-link class="adm2-btn" style="display:inline-block;margin-bottom:18px;">‹ All events</a>
    <div class="adm2-hero">
      <div style="position:relative;">
        <div style="font:500 12px 'IBM Plex Mono',monospace;color:rgba(255,255,255,.72);letter-spacing:.06em;text-transform:uppercase;">Live · ${escape((event.status || '').toUpperCase())}</div>
        <div class="adm2-hero-name" style="margin-top:6px;">${escape(event.title)}</div>
        <div class="adm2-hero-sub">
          <span>${escape(fmtInt(event.participants_count))} participants</span>
          <span>${escape(fmtKobo(event.prize_kobo))} prize pool</span>
          <span>${escape(fmtRelTime(event.finished_at || event.scheduled_at))}</span>
        </div>
      </div>
    </div>

    <div class="adm2-section-label">House economics</div>
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(210px,1fr));">
      ${_statCard('Gross prize pool', fmtKobo(grossKobo))}
      ${_statCard('Distributed', fmtKobo(distributedKobo))}
      ${_statCard('House cut', fmtKobo(houseCutKobo))}
      ${_statCard('House cut %', fmtBps(houseCutBps))}
      ${_statCard('Bonus split', fmtBps(bonusBps))}
      ${_entryRevenueCard(entryRevenueKobo)}
    </div>

    <div class="adm2-section-label">Audience</div>
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(210px,1fr));">
      ${_powerupCard('Participants', participantCount, '🧑‍🤝‍🧑', 'unique users who joined this event')}
      ${_powerupCard('New users', newParticipantCount, '🆕', 'first-ever live trivia join')}
    </div>

    <div class="adm2-section-label">Power-ups used in this event</div>
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(210px,1fr));">
      ${_powerupCard('Erasers used', erasersUsed, '🩹', `est. ${fmtKobo(eraserRevEstKobo)} · ${fmtKobo(avgEraserPriceKobo)}/unit`)}
      ${_powerupCard('50/50 used', fiftyUsed, '⚗️', `est. ${fmtKobo(fiftyRevEstKobo)} · ${fmtKobo(avgFiftyPriceKobo)}/unit`)}
    </div>

    <div class="adm2-section-label">Estimated revenue for this event</div>
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(220px,1fr));">
      ${_revStatCard('Entry fees · net', fmtKobo(entryRevenueKobo), 'paid joins − refunds')}
      ${_revStatCard('Rake · house cut', fmtKobo(houseCutKobo), `${fmtBps(houseCutBps)} of prize pool`)}
      ${_revStatCard('Power-up revenue', fmtKobo(powerupRevEstKobo), 'est · units used × avg unit price')}
      ${_grossRevenueCard(grossRevenueKobo)}
    </div>

    <div class="adm2-panel" style="margin-top:18px;padding:0;overflow:hidden;">
      <div style="padding:18px 20px;border-bottom:1px solid var(--a-border);">
        <div class="adm2-panel-title">All participants</div>
        <div class="adm2-panel-sub">every user who joined — including non-scorers · practice count = solo sessions played on this trivia's categories · click a header to sort</div>
      </div>
      <div id="adm2-live-participants" style="padding:8px 4px;"></div>
    </div>
  `;
}

function _wireDrilldown(host /* , ctx */) {
  // The drill-down is mounted after the payload has already resolved
  // in _renderDrilldown; we re-fetch here to hand the participant
  // array to the sortable-table helper without needing to plumb it
  // through the outer HTML string. This endpoint is admin-gated,
  // participant-list sized (dozens per event today), and fast.
  const listHost = host.querySelector('#adm2-live-participants');
  if (!listHost) return;
  const liveIdMatch = window.location.pathname.match(/^\/admin\/v2\/live\/([^/]+)/);
  if (!liveIdMatch) return;
  const liveId = liveIdMatch[1];
  api(`/admin/live/${encodeURIComponent(liveId)}/participants`)
    .then((participants) => {
      _mountParticipantsTable(listHost, participants);
    })
    .catch((err) => {
      listHost.innerHTML = errorState(err);
    });
}

function _mountParticipantsTable(host, participants) {
  if (!participants || !participants.length) {
    host.innerHTML = `<div style="padding:32px;text-align:center;color:var(--a-faint);">No participants recorded for this event.</div>`;
    return;
  }
  const columns = [
    {
      key: 'rank',
      label: 'Rank',
      get: (p) => (p.rank == null ? 9999 : Number(p.rank)),
      cell: (p) =>
        p.rank != null
          ? `<span class="mono">#${escape(String(p.rank))}</span>`
          : `<span class="mono" style="color:var(--a-faint);">—</span>`,
      className: 'mono',
    },
    {
      key: 'player',
      label: 'Player',
      text: true,
      get: (p) => (p.username || p.email || p.user_id || '').toLowerCase(),
      cell: (p) => `
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
          <span>${escape(p.username || (p.email || '').split('@')[0] || 'player')}</span>
          ${p.is_new_user ? '<span class="adm2-chip g">NEW</span>' : ''}
        </div>
        <div style="font:400 11px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-top:3px;">${escape(p.email || '')}</div>
      `,
    },
    {
      key: 'points',
      label: 'Points',
      right: true,
      get: (p) => Number(p.points || 0),
      cell: (p) => escape(fmtInt(p.points)),
      className: 'mono',
    },
    {
      key: 'correct',
      label: 'Correct',
      right: true,
      get: (p) => Number(p.correct || 0),
      cell: (p) => `${escape(fmtInt(p.correct))}<span style="color:var(--a-faint);"> / ${escape(fmtInt(p.answered))}</span>`,
      className: 'mono',
    },
    {
      key: 'total_ms',
      label: 'Total ms',
      right: true,
      get: (p) => Number(p.total_ms || 0),
      cell: (p) => `${escape((p.total_ms || 0).toLocaleString('en-NG'))} ms`,
      className: 'mono',
    },
    {
      key: 'practice',
      label: 'Practice',
      right: true,
      get: (p) => Number(p.practice_sessions_count || 0),
      cell: (p) => {
        const n = Number(p.practice_sessions_count || 0);
        const tone = n > 0 ? 'var(--a-warm-ink)' : 'var(--a-faint)';
        return `<span class="mono" style="color:${tone};">${escape(fmtInt(n))}</span>`;
      },
      className: 'mono',
    },
    {
      key: 'powerups',
      label: 'Power-ups used',
      right: true,
      get: (p) => Number(p.erasers_used || 0) + Number(p.fiftyfifty_used || 0),
      cell: (p) => {
        const e = Number(p.erasers_used || 0);
        const f = Number(p.fiftyfifty_used || 0);
        if (e === 0 && f === 0) return '<span style="color:var(--a-faint);">—</span>';
        return `<span class="mono" style="color:var(--a-muted);">🩹 ${escape(fmtInt(e))} · ⚗️ ${escape(fmtInt(f))}</span>`;
      },
      className: 'mono',
    },
    {
      key: 'suspicion',
      label: 'Suspicion',
      right: true,
      get: (p) => (p.suspicion_score == null ? -1 : Number(p.suspicion_score)),
      cell: (p) => {
        // Legacy rows (finished before anti-cheat shipped) have no
        // score at all — render as em-dash rather than "0.00" so it's
        // visually distinct from a clean 0.0 score. Not clickable
        // because there's no breakdown to drill into.
        if (p.suspicion_score == null) {
          return '<span style="color:var(--a-faint);">—</span>';
        }
        const s = Number(p.suspicion_score);
        const mult = p.penalty_multiplier == null ? 1.0 : Number(p.penalty_multiplier);
        // Two-tone semantic colour: green if the penalty passed through
        // unchanged, amber if lightly penalised, pink once we start
        // actually shaving points off the payout ranking.
        let tone = 'var(--a-faint)';
        if (mult >= 0.99) tone = 'var(--a-ok)';
        else if (mult >= 0.5) tone = 'var(--a-warm-ink)';
        else tone = 'var(--a-danger, #E94BA2)';
        const penaltyLabel = mult < 0.99
          ? ` · −${((1 - mult) * 100).toFixed(0)}%`
          : '';
        // Click target opens a modal with the full per-factor
        // breakdown. `data-suspicion-user-id` lets the outer table
        // dispatch the click handler without threading the participant
        // object through each row's onclick attribute (which would
        // require JSON.stringify + escaping the whole payload).
        return `<button type="button"
                        class="adm2-suspicion-cell"
                        data-suspicion-user-id="${escape(p.user_id)}"
                        title="Click for factor breakdown"
                        style="background:none;border:none;padding:0;font:inherit;color:${tone};cursor:pointer;text-decoration:underline dotted;text-underline-offset:3px;">${s.toFixed(2)}${escape(penaltyLabel)}</button>`;
      },
      className: 'mono',
    },
    {
      // Client-integrity signal roll-up (is_trusted / decoy_hit /
      // overlay / vis-changes) — advisory only, same aggregates the
      // duel + retest surfaces render. Sorts worst-first via
      // integritySortKey so a review pass can pull bot-tell rows to
      // the top.
      key: 'integrity',
      label: 'Integrity signals',
      get: (p) => integritySortKey(p.integrity_aggregates),
      cell: (p) => integrityChips(p.integrity_aggregates, { compact: true }),
    },
    {
      key: 'payout',
      label: 'Payout',
      right: true,
      get: (p) => Number(p.payout_kobo || 0),
      cell: (p) => {
        const k = Number(p.payout_kobo || 0);
        return k > 0
          ? `<span style="color:var(--a-ok);">${escape(fmtKobo(k))}</span>`
          : '<span style="color:var(--a-faint);">—</span>';
      },
      className: 'mono',
    },
  ];
  mountSortableTable(host, {
    columns,
    rows: participants,
    defaultSort: { key: 'rank', dir: 'asc' },
    minWidth: 1100,
    pageSizeKey: 'live.participants',
  });

  // Click delegation for the Suspicion column drill-down. Bound on the
  // outer host (not the buttons themselves) because mountSortableTable
  // re-renders rows on sort/paginate — a per-button listener would
  // detach silently.
  const idToParticipant = new Map(participants.map((p) => [p.user_id, p]));
  host.addEventListener('click', (ev) => {
    const btn = ev.target.closest('button.adm2-suspicion-cell');
    if (!btn) return;
    const uid = btn.dataset.suspicionUserId;
    const p = uid && idToParticipant.get(uid);
    if (p) _openSuspicionDrilldown(p);
  });
}


// ---------------------------------------------------------------------------
// Suspicion drill-down modal
// ---------------------------------------------------------------------------

/** Human-readable copy for each factor. Kept here (not in i18n or a
 *  registered catalog) because it's admin-only English text — same
 *  reasoning as the labels in the anti_cheat.js settings page. */
const _FACTOR_META = {
  answer_time_anomaly: {
    label: 'Answer-time anomaly',
    hint: 'Share of the player\'s answers that landed at or below the P1 (1st-percentile) speed for their question.',
  },
  time_difficulty_decoupling: {
    label: 'Time × difficulty decoupling',
    hint: 'Humans slow on hard questions; bots don\'t. Higher = weaker correlation between answer time and question difficulty.',
  },
  timing_uniformity: {
    label: 'Timing uniformity',
    hint: 'Low variance across per-question answer times. Bots often lock into a tight response window; humans spread wider.',
  },
  hard_accuracy_anomaly: {
    label: 'Hard-question accuracy',
    hint: 'Excess accuracy on the trivia\'s hardest questions vs the average of everyone who saw them.',
  },
  new_account: {
    label: 'New account',
    hint: 'Hours since sign-up — accounts under 24h saturate this factor.',
  },
  zero_practice: {
    label: 'Zero practice',
    hint: 'No solo / practice matches in the trivia\'s categories in the past 30 days.',
  },
  zero_powerup_engagement: {
    label: 'Zero power-up engagement',
    hint: 'No power-up grants (purchase, referral, awarded, admin) in the past 30 days.',
  },
};

// Palette — hardcoded because the modal mounts on document.body, OUTSIDE
// the `.adm2` container that scopes admin_v2's CSS variables. Referencing
// var(--a-panel) etc. from body-level element resolves to nothing and the
// modal turns transparent. If admin_v2 ever gets a light-mode toggle we'd
// need to plumb the mode through here too, but the current build is
// dark-only for the drill-down.
const _MODAL_BG = '#141D30';         // panel
const _MODAL_BG_ALT = '#1B2438';     // panel-2 (footer strip)
const _MODAL_BORDER = '#2A3650';     // border between rows
const _MODAL_INK = '#E9EEF7';        // primary text
const _MODAL_MUTED = '#8595B2';      // secondary text
const _MODAL_FAINT = '#66758F';      // labels + hints
const _MODAL_TRACK = '#222C42';      // empty bar background
const _MODAL_OK = '#34D399';         // green — clean
const _MODAL_WARN = '#FBBF24';       // amber — mid
const _MODAL_BAD = '#F87171';        // red — flagged

// Thresholds for the per-factor flag classification. Kept in one place so
// the summary counters and the row labels never drift from each other.
const _FLAG_RED_RAW = 0.5;    // raw factor value at/above this = red flag
const _FLAG_GREEN_RAW = 0.15; // raw factor value at/below this = green flag

function _classifyFactor(raw) {
  if (raw >= _FLAG_RED_RAW) return { kind: 'red', color: _MODAL_BAD, glyph: '🚩', label: 'Red flag' };
  if (raw <= _FLAG_GREEN_RAW) return { kind: 'green', color: _MODAL_OK, glyph: '✅', label: 'Clear' };
  return { kind: 'amber', color: _MODAL_WARN, glyph: '⚠️', label: 'Watch' };
}

function _openSuspicionDrilldown(p) {
  const overlay = document.createElement('div');
  overlay.className = 'adm2-modal-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(4,8,16,0.75);z-index:1000;display:flex;align-items:flex-start;justify-content:center;padding:32px 16px;overflow-y:auto;';
  const close = () => overlay.remove();
  overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });
  document.addEventListener('keydown', function onKey(ev) {
    if (ev.key === 'Escape') {
      close();
      document.removeEventListener('keydown', onKey);
    }
  });

  const score = Number(p.suspicion_score || 0);
  const mult = p.penalty_multiplier == null ? 1.0 : Number(p.penalty_multiplier);
  const reduction = Math.max(0, 1 - mult);
  // Colour + label = same semantic tone as the leaderboard cell so the
  // modal header inherits the visual signal the admin clicked into.
  let toneColor = _MODAL_OK;
  let toneLabel = 'Clear';
  let toneBg = 'rgba(52,211,153,0.14)';
  if (mult < 0.5) {
    toneColor = _MODAL_BAD;
    toneLabel = 'Heavy penalty';
    toneBg = 'rgba(248,113,113,0.14)';
  } else if (mult < 0.99) {
    toneColor = _MODAL_WARN;
    toneLabel = 'Light penalty';
    toneBg = 'rgba(251,191,36,0.14)';
  }

  const breakdown = p.suspicion_breakdown || {};
  const factors = breakdown.factors || {};
  const contributions = breakdown.contributions || {};
  const weights = breakdown.weights || {};
  // Sort rows by contribution size, descending, so the biggest drivers
  // of the score sit at the top of the modal. Factors not in the
  // config's known key set (schema drift) still render at the bottom.
  const factorKeys = Object.keys({ ..._FACTOR_META, ...factors });
  factorKeys.sort((a, b) => Number(contributions[b] || 0) - Number(contributions[a] || 0));

  // Summary counters — how many factors flagged which way. Rendered as
  // pills up top so the admin gets the shape of the score before diving
  // into individual rows.
  let redCount = 0, greenCount = 0, amberCount = 0;
  for (const key of factorKeys) {
    const raw = Number(factors[key] || 0);
    const cls = _classifyFactor(raw);
    if (cls.kind === 'red') redCount++;
    else if (cls.kind === 'green') greenCount++;
    else amberCount++;
  }

  const rowsHtml = factorKeys.map((key) => {
    const meta = _FACTOR_META[key] || { label: key, hint: '' };
    const raw = Number(factors[key] || 0);
    const weight = Number(weights[key] || 0);
    const contrib = Number(contributions[key] || 0);
    const cls = _classifyFactor(raw);
    // Bar shows raw factor value (0..1). Uses the flag tone so a
    // red-flagged row's bar reads red, green's reads green. More
    // faithful to "how bad is this signal" than a % of composite
    // (which would let a zero-weight red flag look small).
    const barPct = Math.min(100, Math.max(0, raw * 100));
    return `
      <div style="padding:14px 16px;border-bottom:1px solid ${_MODAL_BORDER};">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;">
          <div style="flex:1;min-width:0;">
            <div style="display:flex;align-items:center;gap:8px;">
              <span title="${escape(cls.label)}" style="font-size:14px;line-height:1;flex-shrink:0;">${cls.glyph}</span>
              <span style="font-weight:600;font-size:13px;color:${_MODAL_INK};">${escape(meta.label)}</span>
              <span style="font-size:9.5px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:${cls.color};padding:2px 6px;border-radius:4px;background:${cls.kind === 'red' ? 'rgba(248,113,113,0.14)' : cls.kind === 'green' ? 'rgba(52,211,153,0.14)' : 'rgba(251,191,36,0.14)'};">${escape(cls.label)}</span>
            </div>
            <div style="color:${_MODAL_MUTED};font-size:11.5px;line-height:1.45;margin-top:4px;">${escape(meta.hint)}</div>
          </div>
          <div class="mono" style="text-align:right;flex-shrink:0;color:${_MODAL_INK};">
            <div style="font-size:13px;color:${cls.color};font-weight:700;">
              +${contrib.toFixed(3)}
            </div>
            <div style="font-size:10.5px;color:${_MODAL_FAINT};margin-top:2px;">
              raw ${raw.toFixed(2)} × w ${weight.toFixed(2)}
            </div>
          </div>
        </div>
        <div style="height:6px;background:${_MODAL_TRACK};border-radius:3px;margin-top:10px;overflow:hidden;">
          <div style="height:100%;width:${barPct}%;background:${cls.color};border-radius:3px;transition:width 0.3s;"></div>
        </div>
      </div>
    `;
  }).join('');

  overlay.innerHTML = `
    <div style="max-width:580px;width:100%;padding:0;overflow:hidden;background:${_MODAL_BG};border:1px solid ${_MODAL_BORDER};border-radius:14px;box-shadow:0 24px 60px rgba(0,0,0,0.6);color:${_MODAL_INK};">
      <div style="padding:20px;border-bottom:1px solid ${_MODAL_BORDER};background:${_MODAL_BG};">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;">
          <div style="min-width:0;">
            <div style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">
              Suspicion drill-down
            </div>
            <div style="font-family:'IBM Plex Sans',sans-serif;font-size:16px;color:${_MODAL_INK};font-weight:700;margin-top:4px;">
              ${escape(p.username || (p.email || '').split('@')[0] || p.user_id)}
            </div>
            <div style="font-size:11.5px;color:${_MODAL_FAINT};margin-top:2px;">${escape(p.email || '')}</div>
          </div>
          <button type="button" data-close style="padding:6px 12px;font-size:13px;flex-shrink:0;background:${_MODAL_TRACK};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;cursor:pointer;">✕</button>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-top:16px;">
          <div>
            <div style="font-size:10px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Composite</div>
            <div class="mono" style="font-size:22px;font-weight:700;margin-top:2px;color:${toneColor};">${score.toFixed(3)}</div>
          </div>
          <div>
            <div style="font-size:10px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Multiplier</div>
            <div class="mono" style="font-size:22px;font-weight:700;margin-top:2px;color:${_MODAL_INK};">${mult.toFixed(2)}</div>
          </div>
          <div>
            <div style="font-size:10px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Reduction</div>
            <div class="mono" style="font-size:22px;font-weight:700;margin-top:2px;color:${toneColor};">−${(reduction * 100).toFixed(0)}%</div>
          </div>
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:14px;">
          <div style="padding:6px 12px;border-radius:6px;background:${toneBg};font-size:11.5px;font-weight:700;color:${toneColor};">
            ${escape(toneLabel)}
          </div>
          <div style="padding:6px 12px;border-radius:6px;background:rgba(248,113,113,0.14);font-size:11.5px;font-weight:700;color:${_MODAL_BAD};">
            🚩 ${redCount} red flag${redCount === 1 ? '' : 's'}
          </div>
          <div style="padding:6px 12px;border-radius:6px;background:rgba(52,211,153,0.14);font-size:11.5px;font-weight:700;color:${_MODAL_OK};">
            ✅ ${greenCount} clear
          </div>
          ${amberCount > 0 ? `<div style="padding:6px 12px;border-radius:6px;background:rgba(251,191,36,0.14);font-size:11.5px;font-weight:700;color:${_MODAL_WARN};">
            ⚠️ ${amberCount} watch
          </div>` : ''}
        </div>
      </div>
      <div style="max-height:60vh;overflow-y:auto;background:${_MODAL_BG};">
        <div style="padding:14px 16px 4px;font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">
          Factor contributions (biggest first)
        </div>
        ${rowsHtml}
      </div>
      <div style="padding:14px 16px;border-top:1px solid ${_MODAL_BORDER};background:${_MODAL_BG_ALT};">
        <div style="font-size:11px;color:${_MODAL_MUTED};line-height:1.55;">
          Composite = Σ (raw × weight) across all factors, clamped to [0, 1].
          Multiplier applied to player's raw points before ranking. Red
          flag = raw ≥ ${_FLAG_RED_RAW}; Clear = raw ≤ ${_FLAG_GREEN_RAW};
          Watch = between. Tune weights + curve on the Anti-cheat
          settings page.
        </div>
      </div>
    </div>
  `;
  overlay.querySelector('[data-close]').addEventListener('click', close);
  document.body.appendChild(overlay);
}

// ---------------------------------------------------------------------------
// Little card helpers
// ---------------------------------------------------------------------------

function _statCard(label, value) {
  return `
    <div style="background:var(--a-panel);border:1px solid var(--a-border);border-radius:14px;padding:18px;box-shadow:0 1px 2px rgba(16,24,40,.05);">
      <div style="font:600 11px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-muted);">${escape(label)}</div>
      <div style="font:600 24px 'IBM Plex Mono',monospace;color:var(--a-ink);margin-top:11px;">${escape(value)}</div>
    </div>
  `;
}

function _entryRevenueCard(kobo) {
  return `
    <div style="position:relative;overflow:hidden;background:var(--a-panel);border:1px solid var(--a-ok-bd);border-radius:14px;padding:18px;box-shadow:0 1px 2px rgba(16,24,40,.05);">
      <div style="position:absolute;left:0;top:0;bottom:0;width:4px;background:var(--a-ok);"></div>
      <div style="font:600 11px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-muted);">Entry revenue</div>
      <div style="font:600 24px 'IBM Plex Mono',monospace;color:var(--a-ok);margin-top:11px;">${escape(fmtKobo(kobo))}</div>
      <div style="font:500 10px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-top:6px;">paid joins − refunds</div>
    </div>
  `;
}

function _revStatCard(label, value, hint) {
  return `
    <div style="background:var(--a-panel);border:1px solid var(--a-border);border-radius:14px;padding:18px;box-shadow:0 1px 2px rgba(16,24,40,.05);">
      <div style="font:600 11px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-muted);">${escape(label)}</div>
      <div style="font:600 24px 'IBM Plex Mono',monospace;color:var(--a-ink);margin-top:11px;">${escape(value)}</div>
      <div style="font:500 10px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-top:6px;">${escape(hint)}</div>
    </div>
  `;
}

function _grossRevenueCard(kobo) {
  return `
    <div style="position:relative;overflow:hidden;background:var(--a-panel);border:1px solid var(--a-warm-bd);border-radius:14px;padding:18px;box-shadow:0 1px 2px rgba(16,24,40,.05);">
      <div style="position:absolute;left:0;top:0;bottom:0;width:4px;background:var(--a-warm-ink);"></div>
      <div style="font:600 11px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-muted);">Gross revenue</div>
      <div style="font:600 26px 'IBM Plex Mono',monospace;color:var(--a-warm-ink);margin-top:11px;">${escape(fmtKobo(kobo))}</div>
      <div style="font:500 10px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-top:6px;">entry + rake + power-ups</div>
    </div>
  `;
}

function _powerupCard(label, value, icon, hint) {
  return `
    <div style="background:var(--a-panel);border:1px solid var(--a-border);border-radius:14px;padding:18px;box-shadow:0 1px 2px rgba(16,24,40,.05);">
      <div style="display:flex;align-items:center;gap:8px;">
        <span style="font-size:18px;">${escape(icon)}</span>
        <div style="font:600 11px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-muted);">${escape(label)}</div>
      </div>
      <div style="font:600 26px 'IBM Plex Mono',monospace;color:var(--a-ink);margin-top:11px;">${escape(fmtInt(value))}</div>
      <div style="font:500 10px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-top:6px;">${escape(hint)}</div>
    </div>
  `;
}

function _statusTone(status) {
  if (status === 'finished') return 'g';
  if (status === 'live') return 'w';
  if (status === 'cancelled') return 'r';
  return '';
}
